import { SUPPLIER_RISK_LEVEL_OPTIONS } from '#app/routes/mocra+/_form+/components/suppliers/constants.ts'
import { type SupplierEntry } from '#app/routes/mocra+/_form+/components/suppliers/schema.ts'

export type SupplierRiskLevelValue =
	(typeof SUPPLIER_RISK_LEVEL_OPTIONS)[number]['value']

export type SupplierDraft = {
	name: string
	materialScope: string
	riskLevel: SupplierRiskLevelValue
	qualityAgreementSigned: boolean
	contactEmail: string
}

export function createSupplierDraft(
	overrides: Partial<SupplierDraft> = {},
): SupplierDraft {
	return {
		name: '',
		materialScope: '',
		riskLevel:
			SUPPLIER_RISK_LEVEL_OPTIONS.find((option) => option.value === 'high')
				?.value ??
			SUPPLIER_RISK_LEVEL_OPTIONS[0]?.value ??
			'critical',
		qualityAgreementSigned: false,
		contactEmail: '',
		...overrides,
	}
}

export function sanitizeSupplierDraft(draft: SupplierDraft): SupplierDraft {
	const riskLevel = isSupplierRiskLevelValue(draft.riskLevel)
		? draft.riskLevel
		: (SUPPLIER_RISK_LEVEL_OPTIONS[0]?.value ?? 'critical')
	return {
		name: draft.name.trim(),
		materialScope: draft.materialScope.trim(),
		riskLevel,
		qualityAgreementSigned: Boolean(draft.qualityAgreementSigned),
		contactEmail: draft.contactEmail.trim(),
	}
}

export function collectSupplierErrors(draft: SupplierDraft): string[] {
	const sanitized = sanitizeSupplierDraft(draft)
	const errors: string[] = []
	if (!sanitized.name) {
		errors.push('Enter the supplier or manufacturer name.')
	}
	if (!sanitized.materialScope) {
		errors.push('Describe the material or service they provide.')
	}
	if (sanitized.contactEmail && !isValidEmailAddress(sanitized.contactEmail)) {
		errors.push('Enter a valid contact email.')
	}
	return errors
}

export function supplierDraftToEntry(draft: SupplierDraft): SupplierEntry {
	const sanitized = sanitizeSupplierDraft(draft)
	return {
		name: sanitized.name,
		materialScope: sanitized.materialScope,
		riskLevel: sanitized.riskLevel,
		qualityAgreementSigned: sanitized.qualityAgreementSigned,
		...(sanitized.contactEmail ? { contactEmail: sanitized.contactEmail } : {}),
	}
}

export function supplierEntryToDraft(entry: SupplierEntry): SupplierDraft {
	const riskLevel = isSupplierRiskLevelValue(entry.riskLevel)
		? entry.riskLevel
		: (SUPPLIER_RISK_LEVEL_OPTIONS[0]?.value ?? 'critical')
	return sanitizeSupplierDraft({
		name: entry.name,
		materialScope: entry.materialScope,
		riskLevel,
		qualityAgreementSigned: Boolean(entry.qualityAgreementSigned),
		contactEmail: entry.contactEmail ?? '',
	})
}

export function supplierCardTitle(
	draft: SupplierDraft,
	_index: number,
): string {
	const sanitized = sanitizeSupplierDraft(draft)
	const name = sanitized.name || 'Unnamed supplier'
	return name
}

export function formatSupplierSummary(draft: SupplierDraft): string {
	const sanitized = sanitizeSupplierDraft(draft)
	const parts: string[] = []
	const riskLabel = resolveSupplierRiskLevelLabel(sanitized.riskLevel)
	parts.push(`Risk: ${riskLabel}`)
	if (sanitized.materialScope) {
		parts.push(sanitized.materialScope)
	}
	parts.push(
		sanitized.qualityAgreementSigned
			? 'QA agreement signed'
			: 'QA agreement pending',
	)
	if (sanitized.contactEmail) {
		parts.push(sanitized.contactEmail)
	}
	return parts.join(' • ')
}

export function resolveSupplierRiskLevelLabel(
	value: SupplierRiskLevelValue,
): string {
	return (
		SUPPLIER_RISK_LEVEL_OPTIONS.find((option) => option.value === value)
			?.label ?? value
	)
}

export function isSupplierRiskLevelValue(
	value: unknown,
): value is SupplierRiskLevelValue {
	return SUPPLIER_RISK_LEVEL_OPTIONS.some((option) => option.value === value)
}

function isValidEmailAddress(value: string): boolean {
	if (!value) return true
	// Basic email validation check; format is further validated on submission.
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}
